/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import {
  ToggleSwitchChangeEvent,
  ToggleSwitchModule
} from 'primeng/toggleswitch';
import { UserAvatarComponent } from 'src/app/components/admin/users/user-avatar/user-avatar.component';
import { GenericColumnConfiguration } from 'src/app/models/GenericColumnConfiguration';
import { GenericTableConfiguration } from 'src/app/models/GenericTableConfiguration';
import { AuthService } from 'src/app/services/auth/auth.service';
import { MessageCenterService } from 'src/app/services/message-center.service';
import { SHARED_IMPORTS } from 'src/app/utils/other/standalone-import';
import { AppAction } from 'src/config/authorization.config';
import { DynamicColumnCompComponent } from '../dynamic-column-comp/dynamic-column-comp.component';
import { TooltipModule } from 'primeng/tooltip';
import { FilesComponent } from '../../file-upload/files/files.component';
import { PopoverModule } from 'primeng/popover';

@Component({
  selector: 'app-generic-column-body',
  templateUrl: './generic-column-body.component.html',
  styleUrls: ['./generic-column-body.component.scss'],
  standalone: true,
  imports: [
    SHARED_IMPORTS,
    ToggleSwitchModule,
    UserAvatarComponent,
    DynamicColumnCompComponent,
    TooltipModule,
    FilesComponent,
    PopoverModule
  ]
})
export class GenericColumnBodyComponent implements OnInit {
  private messageCenterService = inject(MessageCenterService);
  private translate = inject(TranslateService);
  private authService = inject(AuthService);

  @Input() column!: GenericColumnConfiguration;

  @Input() config!: GenericTableConfiguration;

  // This is a hack to always use the formatter function
  // eslint-disable-next-line @angular-eslint/no-input-rename
  @Input({ alias: 'value' }) _value: any;

  @Input() object: any;

  @Input() field: any;

  @Output() switchValueChanged = new EventEmitter<any>();

  @Output() columnClickedEvent = new EventEmitter<any>();

  checked?: boolean;

  ngOnInit(): void {
    this.checked = this.value;
  }

  // This is a hack to always use the formatter function
  // TODO: This should be properly implemented in the future
  get value(): any {
    return this.column.formatter(this._value);
  }

  /**
   * Handles the value change event for a switch input.
   * If the column requires confirmation for cell actions, it prompts the user to confirm the change.
   * If the user confirms, it updates the object's field with the new value and emits the switchValueChanged event.
   * If the user cancels, it shows an informational toast and reverts the switch state.
   * If no confirmation is required, it directly updates the object's field and emits the switchValueChanged event.
   *
   * @param {ToggleSwitchChangeEvent | any} event - The event containing the new value.
   * @returns {Promise<void>} - A promise that resolves when the value change is handled.
   */
  async onSwitchValueChanged(
    event: ToggleSwitchChangeEvent | any
  ): Promise<void> {
    // Check if the column requires confirmation for cell actions
    if (this.column.confirmCellAction) {
      // Prompt the user to confirm the change
      const confirmed = await new Promise<boolean>((resolve) => {
        this.messageCenterService.confirm(
          this.translate.instant(
            `${this.config.translationKey}.table.columns.${this.column.field}.cellConfirm.header`
          ),
          this.translate.instant(
            `${this.config.translationKey}.table.columns.${this.column.field}.cellConfirm.message`
          ),
          () => {
            resolve(true);
          },
          () => {
            const result = 'info';
            const toastPath = `${this.config.translationKey}.table.columns.${this.column.field}.toasts.${result}`;

            // Show an informational toast if the user cancels
            this.messageCenterService.showToast(
              this.translate.instant(`${toastPath}.summary`),
              this.translate.instant(`${toastPath}.detail`),
              result
            );
            resolve(false);
          }
        );
      });

      // If the user confirms, update the object's field and emit the switchValueChanged event
      if (confirmed) {
        this.object[this.field] = event;
        this.switchValueChanged.emit(this.object);
      } else {
        // If the user cancels, revert the switch state
        this.checked = !event;
      }
    } else {
      // If no confirmation is required, update the object's field and emit the switchValueChanged event
      this.object[this.field] = event;
      this.switchValueChanged.emit(this.object);
    }
  }

  /**
   * Handles the value change event without confirmation.
   * Updates the object's referring key with the new value(s) from the event.
   * Emits the switchValueChanged event with the updated object.
   *
   * @param {ToggleSwitchChangeEvent | any} event - The event containing the new value(s).
   * @returns {void}
   */
  valueChangedWithoutConfirm(event: ToggleSwitchChangeEvent | any): void {
    // Check if the column has a referring key
    if (this.column.referringKey) {
      // If the event is an array, append the new values to the existing values
      if (event instanceof Array) {
        this.object[this.column.referringKey] = [
          this.object[this.column.referringKey],
          ...event
        ];
      } else {
        // Otherwise, set the referring key to the new value
        this.object[this.column.referringKey] = event;
      }
    }

    // Emit the switchValueChanged event with the updated object
    this.switchValueChanged.emit(this.object);
  }

  /**
   * Checks if the current user has the required permissions for a given action.
   * If no subject or action is provided, it returns true by default.
   *
   * @param {AppAction | AppAction[] | undefined} action - The action or actions to check permissions for.
   * @returns {boolean} - True if the user has the required permissions, false otherwise.
   */
  $can(action: AppAction | AppAction[] | undefined): boolean {
    // Get the permission subject from the configuration
    const subject = this.config.permissionSubject;

    // If no subject or action is provided, return true by default
    if (!subject || !action) {
      return true;
    }

    // Check if the user has the required permissions for the given action and subject
    return this.authService.$can(action, subject);
  }

  /**
   * Handles the click event on a column.
   * If the column is clickable, it emits the columnClickedEvent with the current object.
   *
   * @returns {void}
   */
  columnClicked(): void {
    // Check if the column is clickable
    if (this.column.isClickable) {
      // Emit the columnClickedEvent with the current object
      this.columnClickedEvent.emit(this.object);
    }
  }

  get showCheck(): boolean {
    return this.value !== this.column.booleanIsOpposite;
  }
}
